import { Ref, reactive, readonly, ref } from '@vue/composition-api';

import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import store from '@/renderer/store';
import { i18n_t } from '@/locales/i18n';

export interface TeamSpaceInfo {
  type: 'team';
  teamId: number;
}
export interface GroupSpaceInfo {
  type: 'group';
  groupId: number;
  spaceOrgId: number;
}

export interface OtherSpaceInfo {
  type: 'other';
}

export type SpaceInfoProp = TeamSpaceInfo | GroupSpaceInfo | OtherSpaceInfo;

export default (spaceInfo: Ref<SpaceInfoProp>) => {
  const memberData = reactive({
    list: [] as CoFile.TeamOrMemberItem[],
    total: -1,
    hasMore: false,
  });

  const PageSize = 500;
  const currPage = ref(1);

  const loading = ref(false);
  const asyncLoading = ref('');

  const methods = {
    resetData() {
      Object.assign(memberData, {
        list: [],
        total: -1,
        hasMore: false,
      });
      currPage.value = 1;
      loading.value = false;
      asyncLoading.value = '';
    },
    /** 格式化查询团队成员的数据 */
    formatPermissionInTeam(item: SMH.TeamMemberItemData) {
      const permission = {
        uid: `user_${item.id}`,
        name: item.nickname as string,
        role: item.role,
        roleId: Number(item.teamRoleId) as number,
        userId: item.id as number,
        avatar: item.avatar as string,
        phoneNumber: item.phoneNumber,
        country: item.countryCode,
        accountName: item.accountName,
        inactive: item.inactive,
        // 是否勾选
        checked: false,
        // 角色名称
        roleName: '',
        // 文件夹权限
        tag: 0,
        // 是否禁止操作？
        disabledOperate: false,
      };
      return permission;
    },
    /** 格式化查询群组成员的数据 */
    formatPermissionInGroup(item: SMH.ColleborationMemberDetail) {
      const roleName = store.getters.roleIdToNameMap[item.authRoleId] || i18n_t('观察者');

      const permission = {
        uid: `user_${item.id}`,
        name: item.nickname as string,
        roleId: item.authRoleId || store.getters.defaultRoleId,
        role: item.role,
        roleName: item.groupRole === 'owner' ? i18n_t('群主') : roleName,
        tag: 0,
        userId: item.id as number,
        avatar: item.avatar as string,
        countryCode: item.countryCode,
        phoneNumber: item.phoneNumber,
        accountName: item.accountName,
        orgId: item.orgId,
        orgName: item.orgName,
        isExternal: item.isExternal,
        isTemporary: item.isTemporary,
        // 是否勾选
        checked: false,
        // 是否禁止操作？
        disabledOperate: item.groupRole === 'owner',
      };
      return permission;
    },
    // 查询团队成员
    async getMembers(params?: { keyword?: string; pageSize?: number }) {
      if (loading.value || asyncLoading.value === 'loading') return;
      if (memberData.list.length === memberData.total) return;

      try {
        if (memberData.total === -1) {
          loading.value = true;
        } else {
          asyncLoading.value = 'loading';
        }

        let totalNum!: number;
        let list!: CoFile.TeamOrMemberItem[];
        if (spaceInfo.value.type === 'team') {
          const _spaceInfo = spaceInfo.value;
          [totalNum, list] = await methods.getMembersInTeam({ ..._spaceInfo, ...params });
        } else if (spaceInfo.value.type === 'group') {
          const _spaceInfo = spaceInfo.value;
          [totalNum, list] = await methods.getMemberInGroup({ ..._spaceInfo, ...params });
        }
        memberData.list = [...memberData.list, ...list];

        if (memberData.list.length < totalNum) {
          asyncLoading.value = 'load-more';
          memberData.hasMore = true;
        } else {
          asyncLoading.value = '';
          memberData.hasMore = false;
        }
        memberData.total = totalNum;
        currPage.value++;
      } catch (e) {
        tips.error(e as MsgError);
      } finally {
        loading.value = false;
      }
    },
    /** 获取团队中的成员 */
    async getMembersInTeam(params: { teamId: number; keyword?: string; pageSize?: number }) {
      const { data } = await smh.userAPI.queryTeamMember({
        teamId: params.teamId,
        page: currPage.value,
        pageSize: params.pageSize || PageSize,
        keyword: params.keyword,
        withBelongingTeams: true,
        withSpaceUsage: true,
        showPhoneNumber: true,
      });
      const list = data.contents.map(methods.formatPermissionInTeam);
      return [data.totalNum, list] as [number, CoFile.TeamOrMemberItem[]];
    },
    /** 获取协作空间中的成员 */
    async getMemberInGroup(params: { groupId: number; spaceOrgId: number; keyword?: string; pageSize?: number }) {
      const { data } = await smh.userAPI.getCollaborationSpaceMembers({
        groupId: params.groupId,
        spaceOrgId: params.spaceOrgId,
        page: currPage.value,
        pageSize: params.pageSize || PageSize,
        keyword: params.keyword,
      });
      const list = data.contents.map(methods.formatPermissionInGroup);
      return [data.totalNum, list] as [number, CoFile.TeamOrMemberItem[]];
    },
  };

  return {
    loading,
    asyncLoading,
    memberData,
    methods,
  };
};
